import * as React from 'react';
import {createViewConfig, view} from '@sail/engine';
import type {View} from '@sail/engine';
import {BasicIcon, FlagIcon} from '@sail/helm';
import {useLocaleCode} from 'mkt-ssr/lib/globalization/context';
import type {
  Country,
  CountryCode,
  LocaleCode,
} from 'mkt-ssr/lib/globalization/types';
import {styles} from 'mkt-ssr/components/sail/GlobalizationPicker/GlobalizationPickerStyles';
import {GlobalizationPickerCountryLanguage} from 'mkt-ssr/components/sail/GlobalizationPicker/GlobalizationPickerCountryLanguage';
import {PathPrefixes} from 'mkt-ssr/lib/globalization';

export type GlobalizationPickerCountryProps = View.IntrinsicElement<
  'li',
  {
    /**
     * Whether this country is the active globalization country
     */
    isActive: boolean;
    /**
     * The two letter code for this country (e.g. US, CA, etc.)
     */
    country: Country;
    /**
     * The display name for this country (e.g. "United States", "Canada", etc.)
     */
    displayName: string;
    /**
     * The prefix to use for this countries test id
     */
    testIdPrefix?: string;

    setLanguageRef?: (
      el: HTMLAnchorElement | null,
      countryCode: CountryCode,
      localeCode: LocaleCode,
    ) => void;
  }
>;

export const GlobalizationPickerCountryConfig = createViewConfig({
  props: {} as GlobalizationPickerCountryProps,
  name: 'GlobalizationPickerCountry',
});

/**
 * Renders the individual country list item for the GlobalizationPicker
 */
export const GlobalizationPickerCountry =
  GlobalizationPickerCountryConfig.createView((props) => {
    const {
      isActive,
      country,
      displayName,
      testIdPrefix,
      ref,
      setLanguageRef,
      ...rest
    } = props;
    const activeLocale = useLocaleCode();
    const languages = Object.values(PathPrefixes).filter(
      ({country: region}) => {
        return region.code === country.code;
      },
    );

    const renderCountryIcon = () => {
      if (isActive) {
        return (
          <BasicIcon
            icon="circleCheck"
            uses={[
              styles.activeCountryCheckIcon,
              styles.activeCountryCheckIconUnsupportedCss,
            ]}
          />
        );
      }
      return (
        <FlagIcon
          country={country.code}
          uses={[styles.countryFlagIconUnsupportedCss]}
        />
      );
    };

    return (
      <view.li
        {...rest}
        uses={[styles.countryListItem, styles.countryListItemUnsupportedCss]}
      >
        {renderCountryIcon()}
        <view.h6
          uses={[
            styles.countryLabel,
            styles.countryLabelUnsupportedCss,
            isActive && styles.activeCountryLabel,
          ]}
        >
          {displayName}
        </view.h6>
        <view.ul
          uses={[
            styles.countryLanguageList,
            styles.countryLanguageListUnsupportedCss,
          ]}
          aria-label={`Available languages for ${displayName}`}
        >
          {languages.map((language) => {
            return (
              <GlobalizationPickerCountryLanguage
                key={language.prefix}
                country={country}
                isActive={isActive && activeLocale === language.locale.code}
                locale={language.locale}
                displayName={language.locale.localizedName}
                testIdPrefix={testIdPrefix}
                ref={(el: HTMLAnchorElement | null) => {
                  if (setLanguageRef && el) {
                    setLanguageRef(el, country.code, language.locale.code);
                  }
                }}
              />
            );
          })}
        </view.ul>
      </view.li>
    );
  });
